import type React from 'react';
import { type ReactNode, useCallback, useEffect, useRef } from 'react';
import { panelScroller } from './panelSystem.styles';
import { useIsDesktop } from '@/src/lib/hooks/ui/useIsDesktop';

type PanelContainerProps = {
  isVisible: boolean;
  title: string;
  onBack: () => void;
  children: ReactNode;
};

function focusElement(element: HTMLElement | null) {
  if (!element) return;

  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
}

export function PanelContainer({ isVisible, title, onBack, children }: PanelContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<Element | null>(null);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (!isVisible) return;

    previousFocusRef.current = document.activeElement;
    requestAnimationFrame(() => {
      focusElement(containerRef.current);
    });

    return () => {
      // On mobile, restoring focus to the previously tapped listing link can
      // trigger a browser "ensure focused element is visible" scroll nudge
      // after PanelHost has already restored the saved page scroll. Desktop
      // still benefits from focus restoration because panels behave like
      // overlays inside the sidebar and keyboard focus continuity matters.
      if (isDesktop && previousFocusRef.current instanceof HTMLElement) {
        focusElement(previousFocusRef.current);
      }
    };
  }, [isDesktop, isVisible]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onBack();
      }
    },
    [onBack],
  );

  return (
    <div
      ref={containerRef}
      className={panelScroller({ isVisible })}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      {children}
    </div>
  );
}
